// The face of a spell card. The stage and the hand both draw one, so the
// card you see land is the card you hold. The pattern is the card stock.
// Words sit on white so they read; the icon sits on white so it reads.

import { OWNER_NAMES } from '../engine/describe';
import type { Side } from '../engine/types';
import { el } from '../lib/dom';
import { FAMILY_LABELS, POLARITY_LABELS, spellById, spellClasses } from '../spells/library';
import type { SpellId } from '../spells/types';
import { createSideMark } from './SideMark';
import { createSpellIcon } from './SpellIcon';

/** A spell, with the side that drew or cast it. Null for a face of the wheel that nobody drew yet. */
export type ShownCard = { spellId: SpellId; caster: Side | null };

export type SpellCardFace = {
  root: HTMLElement;
  draw: (card: ShownCard, prompt: string) => void;
};

export const createSpellCardFace = (className: string): SpellCardFace => {
  const who = el('span', { className: 'card__who' });

  const plate = el('div', { className: 'card__plate' });

  const name = el('h2', { className: 'card__name' });
  const polarity = el('span', { className: 'card__chip card__chip--polarity' });
  const family = el('span', { className: 'card__chip card__chip--family' });
  const chips = el('div', { className: 'card__chips' }, [polarity, family]);
  const blurb = el('p', { className: 'card__blurb' });
  const text = el('div', { className: 'card__text' }, [name, chips, blurb]);

  const prompt = el('p', { className: 'card__prompt' });
  const root = el('div', { className: `card ${className}` }, [who, plate, text, prompt]);

  let drawnSpell = '';

  const draw = (roll: ShownCard, promptText: string): void => {
    const spell = spellById(roll.spellId);
    root.className = `card ${className} ${spellClasses('card', spell)}`;
    who.hidden = roll.caster === null;
    if (roll.caster !== null) {
      who.replaceChildren(createSideMark(roll.caster), `${OWNER_NAMES[roll.caster]} drew`);
    }
    if (drawnSpell !== spell.id) {
      drawnSpell = spell.id;
      plate.replaceChildren(createSpellIcon(spell.id, 'card__icon'));
    }
    name.textContent = spell.name;
    polarity.textContent = POLARITY_LABELS[spell.polarity];
    family.textContent = FAMILY_LABELS[spell.family];
    blurb.textContent = spell.blurb;
    prompt.textContent = promptText;
    prompt.hidden = promptText === '';
  };

  return { root, draw };
};
